import {isAdmin, isDevEnv, isParent, isSelectiveAiDomains, isStudent, isTeacher} from 'common/HelperFunctions'

import type {CurriculumStatus} from '../api/curriculumApi'
import type {AssistantRole, AssistantTab} from '../types'

/** One-line switch for the Curriculum AI Assistant ("Learning Assistant"), a separate product. */
export const CURRICULUM_ASSISTANT_ENABLED = true

/**
 * One-line switch for where the data comes from:
 * - `api`: the curriculum API (`/api/v1/curriculum`). `GET /status` decides who sees it, so no
 *   environment check here (the backend switches a school on).
 * - `demo`: the built-in demo book (`data/demoBook.ts`), dev builds only, no backend needed.
 */
export const CURRICULUM_DATA_SOURCE = 'api' as 'api' | 'demo'

/**
 * Screens from the approved mockups that the curriculum API does not serve yet (practice, help at
 * home, class quizzes, lesson plans, insights, publisher view). They run on the demo book; set to
 * false to hide them.
 */
export const SHOW_DEMO_ONLY_TABS = false

/** Where SchoolRoutes and StudentRoutes mount the Learning Assistant. */
export const LEARNING_ASSISTANT_PATH = '/learning-assistant'

let cachedStatus: CurriculumStatus | null = null

/** Remembers the last `GET /status` answer for code outside React (the sidebar config). */
export const setCachedStatus = (status: CurriculumStatus) => {
  cachedStatus = status
}

/** Whether the route is registered at all (the page itself waits for `GET /status`). */
export const isCurriculumAssistantRouteOn = (): boolean =>
  CURRICULUM_ASSISTANT_ENABLED && !isSelectiveAiDomains() && (CURRICULUM_DATA_SOURCE === 'api' || isDevEnv())

/**
 * Whether the sidebar shows the Learning Assistant: the route is on and, with the API, `GET /status`
 * said enabled (`useCurriculumStatus` in the sidebar fetches it and re-renders).
 */
export const isCurriculumAssistantEnabled = (): boolean =>
  isCurriculumAssistantRouteOn() && (CURRICULUM_DATA_SOURCE === 'demo' || cachedStatus?.enabled === true)

/**
 * The Learning Assistant persona for the signed-in user. Staff and admins see the principal view
 * (insights first); there is no separate principal user type in the portal.
 */
export const getAssistantRole = (): AssistantRole => {
  if (isStudent()) return 'student'
  if (isParent()) return 'parent'
  if (isTeacher()) return 'teacher'
  return 'principal'
}

/** Tabs served by the curriculum API; the rest run on the demo book. */
const ASK_TABS: AssistantTab[] = ['ask', 'photo', 'explain']
const MANAGE_TABS: AssistantTab[] = ['books']

/**
 * Tabs the user sees, in order; the first one is the landing tab. Ask tabs need `can_ask`, the
 * Book Library (`books`) needs `can_manage` (school admins: only they upload books, everyone uses
 * them). In demo mode the Book Library goes to admins.
 */
export const getAssistantTabs = (
  role: AssistantRole = getAssistantRole(),
  status: Pick<CurriculumStatus, 'can_ask' | 'can_manage'> = {can_ask: true, can_manage: isAdmin()}
): AssistantTab[] => {
  const byRole: Record<AssistantRole, AssistantTab[]> = {
    student: ['ask', 'photo', 'explain', 'practice'],
    parent: ['ask', 'photo', 'home-help'],
    teacher: ['ask', 'photo', 'quizzes', 'lessons', 'dashboard'],
    principal: ['dashboard', 'ask', 'publisher', 'books'],
  }
  const tabs = byRole[role].filter((tab) => {
    if (ASK_TABS.includes(tab)) return status.can_ask
    if (MANAGE_TABS.includes(tab)) return status.can_manage
    // With the API, the dashboard shows live data (DashboardView), not the demo insights.
    if (tab === 'dashboard' && CURRICULUM_DATA_SOURCE === 'api') return true
    return SHOW_DEMO_ONLY_TABS
  })
  // A manager who is not a principal-type user (unlikely) still reaches the Book Library.
  if (status.can_manage && !tabs.includes('books')) tabs.push('books')
  return tabs
}
